import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const EligibleEnterpriseWorkspacesDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"EligibleEnterpriseWorkspaces"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"count"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"Int"}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"enterpriseId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"startIndex"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"Int"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"enterprise"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"enterpriseId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"organizations"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"count"},"value":{"kind":"Variable","name":{"kind":"Name","value":"count"}}},{"kind":"Argument","name":{"kind":"Name","value":"startIndex"},"value":{"kind":"Variable","name":{"kind":"Name","value":"startIndex"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"organizations"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"displayName"}},{"kind":"Field","name":{"kind":"Name","value":"jwmLink"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"idCloud"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"totalOrganizations"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"EligibleEnterpriseWorkspaces","document":EligibleEnterpriseWorkspacesDocument}} as const;
export type EligibleEnterpriseWorkspacesQueryVariables = Types.Exact<{
  count?: Types.InputMaybe<Types.Scalars['Int']['input']>;
  enterpriseId: Types.Scalars['ID']['input'];
  startIndex?: Types.InputMaybe<Types.Scalars['Int']['input']>;
}>;


export type EligibleEnterpriseWorkspacesQuery = (
  { __typename: 'Query' }
  & { enterprise?: Types.Maybe<(
    { __typename: 'Enterprise' }
    & Pick<Types.Enterprise, 'id'>
    & { organizations: (
      { __typename: 'EnterpriseOrganizations' }
      & Pick<Types.EnterpriseOrganizations, 'totalOrganizations'>
      & { organizations: Array<(
        { __typename: 'Organization' }
        & Pick<Types.Organization, 'id' | 'displayName'>
        & { jwmLink?: Types.Maybe<(
          { __typename: 'JwmWorkspaceLink' }
          & Pick<Types.JwmWorkspaceLink, 'idCloud'>
        )> }
      )> }
    ) }
  )> }
);

/**
 * __useEligibleEnterpriseWorkspacesQuery__
 *
 * To run a query within a React component, call `useEligibleEnterpriseWorkspacesQuery` and pass it any options that fit your needs.
 * When your component renders, `useEligibleEnterpriseWorkspacesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEligibleEnterpriseWorkspacesQuery({
 *   variables: {
 *      count: // value for 'count'
 *      enterpriseId: // value for 'enterpriseId'
 *      startIndex: // value for 'startIndex'
 *   },
 * });
 */
export function useEligibleEnterpriseWorkspacesQuery(
  baseOptions: TrelloQueryHookOptions<
    EligibleEnterpriseWorkspacesQuery,
    EligibleEnterpriseWorkspacesQueryVariables
  > &
    (
      | {
          variables: EligibleEnterpriseWorkspacesQueryVariables;
          skip?: boolean;
        }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: EligibleEnterpriseWorkspacesDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    EligibleEnterpriseWorkspacesQuery,
    EligibleEnterpriseWorkspacesQueryVariables
  >(EligibleEnterpriseWorkspacesDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useEligibleEnterpriseWorkspacesLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    EligibleEnterpriseWorkspacesQuery,
    EligibleEnterpriseWorkspacesQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    EligibleEnterpriseWorkspacesQuery,
    EligibleEnterpriseWorkspacesQueryVariables
  >(EligibleEnterpriseWorkspacesDocument, options);
}
export function useEligibleEnterpriseWorkspacesSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        EligibleEnterpriseWorkspacesQuery,
        EligibleEnterpriseWorkspacesQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    EligibleEnterpriseWorkspacesQuery,
    EligibleEnterpriseWorkspacesQueryVariables
  >(EligibleEnterpriseWorkspacesDocument, options);
}
export type EligibleEnterpriseWorkspacesQueryHookResult = ReturnType<
  typeof useEligibleEnterpriseWorkspacesQuery
>;
export type EligibleEnterpriseWorkspacesLazyQueryHookResult = ReturnType<
  typeof useEligibleEnterpriseWorkspacesLazyQuery
>;
export type EligibleEnterpriseWorkspacesSuspenseQueryHookResult = ReturnType<
  typeof useEligibleEnterpriseWorkspacesSuspenseQuery
>;
export type EligibleEnterpriseWorkspacesQueryResult = Apollo.QueryResult<
  EligibleEnterpriseWorkspacesQuery,
  EligibleEnterpriseWorkspacesQueryVariables
>;
